import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse
} from '@mui/material';
import { Search as SearchIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import { AdminPageContainer } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import { getPermissions } from '../../redux/ducks/permission';
import { useDispatch } from 'react-redux';

const SearchSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const ChildExpansionCell = styled(TableCell)({
    paddingTop: 0,
    paddingBottom: 0,
    border: 0,
});

const ChildExpansionWrapper = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(6),
}));

const ChildExpansionLabel = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginBottom: theme.spacing(1),
}));

const ChildChipRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
}));

const AdminPermissions = () => {
    const dispatch = useDispatch();
    const [permissions, setPermissions] = useState([]);
    const [filteredPermissions, setFilteredPermissions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState({});

    const toggleRow = (name) => {
        setExpandedRows((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    useEffect(() => {
        loadPermissions();
    }, []);

    useEffect(() => {
        filterPermissions();
    }, [searchTerm, permissions]);

    const loadPermissions = () => {
        setLoading(true);
        dispatch(getPermissions((data) => {
            setPermissions(data || []);
            setLoading(false);
        }));
    };

    const filterPermissions = () => {
        if (!searchTerm.trim()) {
            setFilteredPermissions(permissions);
        } else {
            const term = searchTerm.toLowerCase();
            setFilteredPermissions(
                permissions.filter(
                    (p) =>
                        p.name?.toLowerCase().includes(term) ||
                        p.description?.toLowerCase().includes(term) ||
                        p.children?.some((child) => child.toLowerCase().includes(term))
                )
            );
        }
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Permissions Catalog"
                subtitle="The permission catalog available to be assigned to roles."
            />

            <SearchSection>
                <TextField
                    fullWidth
                    placeholder="Search permissions by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </SearchSection>

            {loading ? (
                <LoadingBox>
                    <CircularProgress />
                </LoadingBox>
            ) : filteredPermissions.length === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        {searchTerm ? 'No permissions found matching your search.' : 'No permissions available.'}
                    </Typography>
                </EmptyState>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={40} />
                                <TableCell><strong>Category Name</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell align="center"><strong>Children</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPermissions.map((permission) => (
                                <React.Fragment key={permission.name}>
                                    <TableRow hover>
                                        <TableCell>
                                            {permission.children?.length > 0 && (
                                                <IconButton size="small" onClick={() => toggleRow(permission.name)}>
                                                    {expandedRows[permission.name] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {permission.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {permission.description || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={permission.children?.length || 0}
                                                size="small"
                                                color={permission.children?.length > 0 ? 'primary' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                    </TableRow>
                                    {permission.children?.length > 0 && (
                                        <TableRow>
                                            <ChildExpansionCell colSpan={4}>
                                                <Collapse in={expandedRows[permission.name]} timeout="auto" unmountOnExit>
                                                    <ChildExpansionWrapper>
                                                        <ChildExpansionLabel variant="caption" color="text.secondary" gutterBottom>
                                                            Child Permissions:
                                                        </ChildExpansionLabel>
                                                        <ChildChipRow>
                                                            {permission.children.map((child, idx) => (
                                                                <Chip key={idx} label={child} size="small" />
                                                            ))}
                                                        </ChildChipRow>
                                                    </ChildExpansionWrapper>
                                                </Collapse>
                                            </ChildExpansionCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </AdminPageContainer>
    );
};

export default AdminPermissions;
